import React, { useState, useEffect } from 'react';
import { FaEye, FaEyeSlash, FaTimes } from 'react-icons/fa'; // Importing eye and cross icons
import { useNavigate } from 'react-router-dom'; // Importing useNavigate for redirection

function ForgetPassword() {
  const navigate = useNavigate(); // Initialize navigate function
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [isOtpSent, setIsOtpSent] = useState(false);
  const [resendTimer, setResendTimer] = useState(50);
  const [newPassword, setNewPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [statusMessage, setStatusMessage] = useState('');
  const [statusType, setStatusType] = useState(''); // "success" or "error"
  const [isLoading, setIsLoading] = useState(false); // New loading state

  useEffect(() => {
    let timer;
    if (isOtpSent && resendTimer > 0) {
      timer = setInterval(() => {
        setResendTimer((prev) => prev - 1);
      }, 1000);
    }
    return () => clearInterval(timer);
  }, [isOtpSent, resendTimer]);

  const handleSendOtp = async () => {
    if (email) {
      setIsLoading(true); // Start loading when sending OTP
      try {
        const response = await fetch('http://127.0.0.1:8000/forgetResetPass/forgetsend-otp/', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email }),
        });

        if (response.ok) {
          setIsOtpSent(true);
          setResendTimer(30);
          setStatusMessage('OTP has been sent to your email.');
          setStatusType('success');
        } else {
          const error = await response.json();
          setStatusMessage(error.error || 'Failed to send OTP. Please try again.');
          setStatusType('error');
        }
      } catch (err) {
        setStatusMessage('Failed to send OTP. Please try again.');
        setStatusType('error');
      } finally {
        setIsLoading(false); // End loading after the response
      }
    }
  };

  const handleResendOtp = async () => {
    if (resendTimer === 0) {
      handleSendOtp();
    }
  };

  const handleSubmit = async () => {
    if (!otp) {
      setStatusMessage('Please enter the OTP.');
      setStatusType('error');
      return;
    }

    try {
      const otpResponse = await fetch('http://127.0.0.1:8000/forgetResetPass/forgetverify-otp/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, otp }),
      });

      if (otpResponse.ok) {
        if (!newPassword) {
          setStatusMessage('Please enter a new password.');
          setStatusType('error');
          return;
        }

        const resetResponse = await fetch('http://127.0.0.1:8000/forgetResetPass/forgetreset-password/', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email, new_password: newPassword }),
        });

        if (resetResponse.ok) {
          setStatusMessage('Password reset successfully!');
          setStatusType('success');
          setEmail('');
          setOtp('');
          setIsOtpSent(false);
          setNewPassword('');
        } else {
          const resetError = await resetResponse.json();
          setStatusMessage(resetError.error || 'Failed to reset password.');
          setStatusType('error');
        }
      } else {
        const otpError = await otpResponse.json();
        setStatusMessage(otpError.error || 'Invalid OTP.');
        setStatusType('error');
      }
    } catch (err) {
      setStatusMessage('Failed to reset password. Please try again.');
      setStatusType('error');
    }
  };

  // Function to handle cross button click and navigate to Home
  const handleClose = () => {
    navigate('/HomeLogin'); // Redirect to Home page
  };

  return (
    <div className="flex flex-col items-center justify-center h-screen bg-gray-100">
      <div className="w-full max-w-lg p-6 bg-white rounded-lg shadow-md relative">
        {/* Cross Button for Closing */}
        <button
          onClick={handleClose}
          className="absolute top-2 right-2 text-gray-500 hover:text-gray-700"
        >
          <FaTimes size={20} />
        </button>

        <h2 className="text-2xl font-semibold text-center text-gray-700 mb-4">Forgot Password</h2>

        {/* Email Input */}
        <div className="mb-4">
          <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
            Email
          </label>
          <input
            id="email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="Enter your email"
          />
          {statusMessage && (
            <p
              className={`mt-2 text-sm ${statusType === 'success' ? 'text-green-600' : 'text-red-600'}`}
            >
              {statusMessage}
            </p>
          )}
        </div>

        {/* Verify Button */}
        <button
          onClick={handleSendOtp}
          className="w-full px-4 py-2 mb-4 text-white"
          style={{ backgroundColor: '#0077af' }}
          disabled={!email || isOtpSent || isLoading} // Disable if loading or OTP already sent
        >
          {isLoading ? 'Sending OTP...' : 'Verify Email'}
        </button>

        {isOtpSent && (
          <>
            {/* OTP Input */}
            <div className="mb-4">
              <label htmlFor="otp" className="block text-sm font-medium text-gray-700 mb-2">
                OTP
              </label>
              <input
                id="otp"
                type="text"
                value={otp}
                onChange={(e) => setOtp(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Enter OTP"
              />
            </div>

            {/* New Password Input */}
            <div className="mb-4 relative">
              <label htmlFor="new-password" className="block text-sm font-medium text-gray-700 mb-2">
                New Password
              </label>
              <input
                id="new-password"
                type={showPassword ? 'text' : 'password'}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Enter new password"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-9"
              >
                {showPassword ? <FaEyeSlash size={20} /> : <FaEye size={20} />}
              </button>
            </div>

            {resendTimer > 0 ? (
              <p className="text-sm text-gray-500 mb-4">Resend OTP in {resendTimer} seconds</p>
            ) : (
              <button
                onClick={handleResendOtp}
                className="text-sm text-blue-500 hover:underline mb-4"
              >
                Resend OTP
              </button>
            )}
          </>
        )}

        {otp && (
          <button
            onClick={handleSubmit}
            className="w-full px-4 py-2 text-white"
            style={{ backgroundColor: '#0077af' }}
          >
            Submit
          </button>
        )}
      </div>
    </div>
  );
}

export default ForgetPassword;
